/**
 * ProductsLayout V2
 *
 * Layout-Container für den Products-Bereich (PIM) mit Sidebar und Content-Bereich.
 * Zeigt unscharfen Content mit Upgrade-Overlay wenn kein Pro-Abo vorhanden.
 *
 * @accessibility WCAG 2.1 AA + BFSG konform
 */

import { Outlet, useLocation, Link } from 'react-router-dom';
import { ProductsSidebar } from './ProductsSidebar';
import { useActiveSubscription } from '@/hooks/subscription/useActiveSubscription';
import { ArrowRight, Sparkles, Check } from 'lucide-react';
import { ReauthGate } from '@/components/v2/reauth/ReauthGate';

export function ProductsLayout() {
  const location = useLocation();
  const reloadKey = location.state?.reload || 0;
  const { data: subscription, isLoading } = useActiveSubscription();

  const hasProAccess = isLoading || (
    subscription?.status === 'active' &&
    ['pro', 'enterprise'].includes(subscription.tier_code)
  );

  return (
    <ReauthGate>
      <div className="relative flex h-[calc(100vh-56px)]">
        {/* Sidebar */}
        <ProductsSidebar />

        {/* Main Content */}
        <main className={`flex-1 overflow-auto bg-white dark:bg-neutral-950 ${!hasProAccess ? 'blur-sm pointer-events-none select-none' : ''}`}>
          <Outlet key={reloadKey} />
        </main>

        {/* Upgrade Overlay */}
        {!hasProAccess && <ProUpgradeOverlay tierCode={subscription?.tier_code} />}
      </div>
    </ReauthGate>
  );
}

const PRO_FEATURES = [
  'PIM/DAM-System für Produktverwaltung',
  'Multi-Channel Publishing',
  'Unbegrenzte Integrationen (JTL, Shopify, u.a.)',
  'Flexible Produktlimits (1.000–100.000)',
  'Bis zu 10.000 Bilder/Monat',
  '4K Bildgenerierung',
  'API-Zugriff & Batch Generation',
  'Priority Support',
];

function ProUpgradeOverlay({ tierCode }: { tierCode?: string }) {
  const currentPlan = tierCode === 'starter' ? 'Starter' : 'Free';

  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/20 dark:bg-black/40">
      <div className="mx-4 max-w-lg rounded-2xl border border-neutral-200 bg-white p-8 shadow-2xl dark:border-neutral-700 dark:bg-neutral-900">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-emerald-100 dark:bg-emerald-900/30">
            <Sparkles className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
          </div>

          <h2 className="mb-2 text-xl font-bold text-neutral-900 dark:text-neutral-50">
            Pro-Abo erforderlich
          </h2>

          <p className="mb-5 text-sm text-neutral-600 dark:text-neutral-400">
            Die Produktverwaltung ist ab dem <strong>Pro-Plan</strong> verfügbar.
            Ihr aktueller Plan: <strong>{currentPlan}</strong>.
          </p>
        </div>

        <ul className="mb-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {PRO_FEATURES.map((feature) => (
            <li key={feature} className="flex items-start gap-2 text-sm text-neutral-700 dark:text-neutral-300">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
              {feature}
            </li>
          ))}
        </ul>

        <div className="text-center">
          <Link
            to="/v2/credits"
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:bg-emerald-700 hover:shadow-xl focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
          >
            Jetzt auf Pro upgraden
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ProductsLayout;
